import { FC, useState, useEffect, useMemo } from "react";
import { Formik, useFormik } from "formik";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import { t, use } from "i18next";

import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { InputWithSelectHint } from "@/shared/components/atoms/InputWithSelectHint";
import TableListAccess from "@/shared/components/organisms/TableListAccess";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import { DateRangePicker } from "@/shared/components/atoms/DataRangePicker";
import PaginationUI from "@/shared/components/atoms/PaginationUI";
import { InputFile } from "@/shared/components/atoms/InputFile";
import { Select } from "@/shared/components/organisms/Select";
import { toasts } from "@/shared/utils/toasts";

// API
import { useFetchBranchsMutation } from "@/app/branchs/branchs.api";
import { useGymsByAccessUserQuery } from "@/app/gyms/gyms.api";
import { useFetchUsersMutation } from "@/app/users/users.api";
import { useRolesQuery } from "@/app/roles/roles.api";
import {
  useFetchAccessMutation,
  useInstallationsQuery,
  useLathesQuery,
  useOpenDoorRequestMutation,
  useAccessUserManualMutation,
} from "@/app/access/access.api";

// SLICE
import { selectUser } from "@/app/auth/auth.slice";

// SCHEMA
import {
  accessInitialValues,
  accessValidationSchema,
  accessRegisterInitialValues,
  accessRegisterValidationSchema,
} from "@/shared/schemas/access.schema";

export const Access: FC<any> = () => {
  const [page, setPage] = useState(1);
  const user = useSelector(selectUser);
  const [gymId, setgymId] = useState(0);
  const [branchId, setBranchId] = useState(0);
  const [optionsGyms, setOptionsGyms] = useState([]);
  const [optionsBranchs, setOptionsBranchs] = useState([]);
  const [selectedGyms, setSelectedGyms] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [selectBranch, setSelectBranch] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const [access, setAccess] = useState([]);

  useEffect(() => {
    if (user && user.branch) {
      setBranchId(user.branch.id);
    }
  }, [user]);

  const [fetchUsers] = useFetchUsersMutation();
  const [fetchAccess, { isError, isLoading }] = useFetchAccessMutation();

  const [installationId, setInstallationId] = useState(0);
  const [installations, setInstallations] = useState([]);

  const installationsQuery = useInstallationsQuery(
    { branchId: branchId },
    { skip: branchId <= 0 }
  );

  useEffect(() => {
    if (installationsQuery.isSuccess) {
      setInstallations(installationsQuery.data);
    }
  }, [installationsQuery]);

  const [lathes, setLathes] = useState([]);

  const lathesQuery = useLathesQuery(
    { computer_id: installationId },
    { skip: installationId <= 0 }
  );

  useEffect(() => {
    if (lathesQuery.isSuccess) {
      setLathes(lathesQuery.data);
    }
  }, [lathesQuery]);

  const [fetchBranchs] = useFetchBranchsMutation();
  const gymsByAccessUserQuery = useGymsByAccessUserQuery({});

  const rolesQuery = useRolesQuery(
    { branchId: branchId },
    { skip: branchId <= 0 }
  );

  const roleOptions = useMemo(() => {
    return rolesQuery.data?.map((role: any) => ({
      id: role.name,
      name: role.name,
    }));
  }, [rolesQuery]);

  useEffect(() => {
    if (user && gymsByAccessUserQuery.isSuccess) {
      let objetCompanies = {};
      const arrayCompanies = [];
      for (let i = 0; i < gymsByAccessUserQuery.data.length; i++) {
        objetCompanies = {
          id: gymsByAccessUserQuery.data[i].id,
          name: gymsByAccessUserQuery.data[i].name,
        };
        arrayCompanies.push(objetCompanies);
      }

      setgymId(user.branch.gymId);
      setSelectedGyms(
        arrayCompanies.find((gym) => gym.id === user.branch.gymId)
      );
      setOptionsGyms(arrayCompanies);
    }
  }, [gymsByAccessUserQuery, user]);

  useEffect(() => {
    const fetchBranchsData = async () => {
      const responseBranchs = await fetchBranchs({
        limit: 100,
        gymId: gymId,
      }).unwrap();
      const arrayBranchs = [];
      let objetBranchs = {};
      for (let i = 0; i < responseBranchs.data.length; i++) {
        objetBranchs = {
          id: responseBranchs.data[i].id,
          name: responseBranchs.data[i].branch,
          gym_id: responseBranchs.data[i].gym_id,
        };
        arrayBranchs.push(objetBranchs);
      }

      if (gymId === user.branch.gymId) {
        setSelectBranch(
          arrayBranchs.find((branch) => branch.id === user.branch.id)
        );
      }

      setOptionsBranchs(arrayBranchs);
    };

    if (gymId !== 0) {
      fetchBranchsData();
    }
  }, [gymId]);

  const [openDoorRequest, openDoorRequestResult] = useOpenDoorRequestMutation();

  useEffect(() => {
    if (openDoorRequestResult.isSuccess) {
      toasts.success("Exito", "Ingreso permitido");

      fechtDatos();
      playAccessSuccess();
      handleOpenPermitir();
    }

    if (openDoorRequestResult.isError) {
      toasts.danger("Error", "Acceso no permitido");

      fechtDatos();
      handleOpenPermitir();
      playAccessError();
    }
  }, [openDoorRequestResult]);

  const [accessUserManual, accessUserManualResult] =
    useAccessUserManualMutation();

  useEffect(() => {
    if (accessUserManualResult.isSuccess) {
      toasts.success("Exito", "Ingreso permitido");

      playAccessSuccess();
      handleOpenRegistrar();
      fechtDatos();
    }

    if (accessUserManualResult.isError) {
      toasts.danger("Error", t(accessUserManualResult.error.data.message));

      fechtDatos();
      handleOpenRegistrar();
      playAccessError();
    }
  }, [accessUserManualResult]);

  const filterFormik = useFormik({
    initialValues: {
      date_init: DateTime.now().minus({ days: 1 }).toFormat("yyyy-MM-dd"),
      date_end: DateTime.now().toFormat("yyyy-MM-dd"),
      selected_role: "",
      searchFilter: "",
      number_access: 0,
    },
    onSubmit: () => {},
  });

  const [openFilter, setOpenFilter] = useState(false);
  const handleOpenFilter = () => setOpenFilter(!openFilter);

  const [openPermitir, setOpenPermitir] = useState(false);
  const handleOpenPermitir = () => setOpenPermitir(!openPermitir);

  const [openRegistrar, setOpenRegistrar] = useState(false);
  const handleOpenRegistrar = () => setOpenRegistrar(!openRegistrar);

  const [openUpImage, setOpenUpImage] = useState(false);
  const handleOpenUpImage = () => setOpenUpImage(!openUpImage);

  const optionsMenu = [
    { name: t("share.views.up_image"), functionMenu: handleOpenUpImage },
  ];

  const fechtDatos = async () => {
    const response = await fetchAccess({
      gymId: gymId,
      branch_id: selectBranch?.id ?? branchId,
      date_init: filterFormik.values.date_init,
      date_end: filterFormik.values.date_end,
      selected_role: filterFormik.values.selected_role,
      searchFilter: filterFormik.values.searchFilter,
      number_access: filterFormik.values.number_access,
    }).unwrap();

    setPage(1);
    setAccess(response.data);
  };

  useEffect(() => {
    fechtDatos();
  }, [filterFormik.values]);

  useEffect(() => {
    fechtDatos();
  }, [fetchAccess, branchId, selectedGyms]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const [users, setUsers] = useState([]);
  const fechtUsers = async (search) => {
    try {
      const response = await fetchUsers({
        search: search.length < 1 ? undefined : search,
        page: 1,
        limit: 20,
        branch: branchId,
      }).unwrap();

      const arrayUsers = [];
      let objetCompanies = {};

      for (let i = 0; i < response.data.length; i++) {
        objetCompanies = {
          id: response.data[i].id,
          name: response.data[i].name,
          email: response.data[i].email,
        };
        arrayUsers.push(objetCompanies);
      }
      setUsers(arrayUsers);
    } catch (error) {
      console.log(error);
    }
  };

  const playAccessSuccess = () => {
    const audio = new Audio("/assets/media/sonidos/correcto.mp3");
    audio.volume = 0.15;
    audio.play();
  };

  const playAccessError = () => {
    const audio = new Audio("/assets/media/sonidos/error.mp3");
    audio.volume = 0.15;
    audio.play();
  };

  return (
    <div className="flex flex-col items-center h-full w-full mt-8">
      <div className="w-full sm:max-w-full md:max-w-full lg:max-w-full xl:max-w-full 2xl:max-w-full mx-auto mb-8">
        <TableListAccess
          title={t("access.views.title")}
          access={access}
          searchQuery={filterFormik.values.searchFilter}
          onSearchChange={(e) =>
            filterFormik.setFieldValue("searchFilter", e.target.value)
          }
          titleSelectCompany={t("branch.views.company")}
          selectedCompany={selectedGyms}
          setSelectedCompany={(gym) => {
            setSelectedGyms(gym);
            setgymId(gym.id);
          }}
          optionsCompany={optionsGyms}
          titleSelectBranchs={t("branch.views.branch")}
          selectedBranchs={selectBranch}
          setSelectedBranchs={(branch) => {
            setSelectBranch(branch);
            setBranchId(branch.id);
          }}
          optionsBranchs={optionsBranchs}
          optionsMenuEllipses={optionsMenu}
          onClickFilter={handleOpenFilter}
          onClickPermitir={handleOpenPermitir}
          onClickRegistrar={handleOpenRegistrar}
          page={page}
          isLoading={isLoading}
        />

        <PaginationUI
          currentPage={page}
          onPageChange={(event) => handlePageChange(event)}
          pageSize={10}
          totalResults={access.length}
          totalPages={Math.ceil((access.length ?? 0) / 10)}
        />

        <Formik
          enableReinitialize
          validationSchema={accessValidationSchema}
          initialValues={accessInitialValues}
          onSubmit={(values, formikHelpers) => {
            openDoorRequest({
              computer_id: values.instalation,
              lathe_id: values.torniquete,
              branch_id: branchId,
            });
            formikHelpers.setSubmitting(false);
            formikHelpers.resetForm();
          }}
        >
          {({ setFieldValue, values, handleSubmit, errors }) => {
            useEffect(() => {
              if (values.instalation > 0) {
                setInstallationId(values.instalation);
              }
            }, [values.instalation]);

            return (
              <DialogCreate
                open={openPermitir}
                setOpen={handleOpenPermitir}
                tittle={t("share.views.permitir_acceso")}
                tittleButtonCreate={t("share.views.permitir_acceso")}
                tittleButtonCancel={t("general.cancel")}
                OnclickCreate={handleSubmit}
                OnclickCancel={handleOpenPermitir}
              >
                <form action="">
                  <div className="mt-6 mx-2">
                    <div className="space-y-12">
                      <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                        <div className="flex items-center justify-center h-full text-center">
                          <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">
                              {t("share.views.info_access")}
                            </h2>
                          </div>
                        </div>
                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                          <div className="col-span-full">
                            <Select
                              name="instalation"
                              span={t("general.mandatory")}
                              tittle={t("share.views.instalation")}
                              selected={installations.find(
                                (installation) =>
                                  installation.id === values.instalation
                              )}
                              setSelected={(option) =>
                                setFieldValue("instalation", option.id)
                              }
                              options={installations}
                              error={!!errors.instalation}
                              textError={errors.instalation}
                            />
                          </div>
                          <div className="col-span-full">
                            <Select
                              name="torniquete"
                              span={t("general.mandatory")}
                              tittle={t("share.views.torniquete")}
                              selected={lathes.find(
                                (lathe) => lathe.id === values.torniquete
                              )}
                              setSelected={(option) =>
                                setFieldValue("torniquete", option.id)
                              }
                              options={lathes}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </DialogCreate>
            );
          }}
        </Formik>

        <Formik
          enableReinitialize
          validationSchema={accessRegisterValidationSchema}
          initialValues={accessRegisterInitialValues}
          onSubmit={(values, formikHelpers) => {
            accessUserManual({
              computer_id: values.instalation,
              lathe_id: values.torniquete,
              branch_id: branchId,
              user_id: values.user,
            });
            formikHelpers.setSubmitting(false);
            formikHelpers.resetForm();
          }}
        >
          {({ setFieldValue, values, handleSubmit, errors }) => {
            useEffect(() => {
              if (values.instalation > 0) {
                setInstallationId(values.instalation);
              }
            }, [values.instalation]);

            return (
              <DialogCreate
                open={openRegistrar}
                setOpen={handleOpenRegistrar}
                tittle={t("share.views.registrar_acceso")}
                tittleButtonCreate={t("share.views.registrar_acceso")}
                tittleButtonCancel={t("general.cancel")}
                OnclickCreate={handleSubmit}
                OnclickCancel={handleOpenRegistrar}
                footer={
                  <div className="flex justify-end">
                    <button
                      disabled={false}
                      type="button"
                      onClick={handleOpenRegistrar}
                      className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                      {t("general.cancel")}
                    </button>
                  </div>
                }
              >
                <form action="">
                  <div className="mt-6 mx-2">
                    <div className="space-y-12">
                      <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                        <div className="flex items-center justify-center h-full text-center">
                          <div>
                            <h2 className="text-base font-semibold leading-7 text-gray-900">
                              {t("share.views.info_access")}
                            </h2>
                          </div>
                        </div>
                        <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                          <div className="col-span-full">
                            <Select
                              name="instalation"
                              span={t("general.mandatory")}
                              tittle={t("share.views.instalation")}
                              selected={installations.find(
                                (installation) =>
                                  installation.id === values.instalation
                              )}
                              setSelected={(option) =>
                                setFieldValue("instalation", option.id)
                              }
                              options={installations}
                              error={!!errors.instalation}
                              textError={errors.instalation}
                            />
                          </div>
                          <div className="col-span-full">
                            <Select
                              name="torniquete"
                              span={t("general.mandatory")}
                              tittle={t("share.views.torniquete")}
                              selected={lathes.find(
                                (lathe) => lathe.id === values.torniquete
                              )}
                              setSelected={(option) =>
                                setFieldValue("torniquete", option.id)
                              }
                              options={lathes}
                            />
                          </div>
                          <div className="col-span-full">
                            <InputWithSelectHint
                              name="user"
                              tittle={t("share.views.user")}
                              span={t("general.mandatory")}
                              disabled={false}
                              setSelected={(user) => {
                                setFieldValue("user", user?.id);
                                handleSubmit();
                              }}
                              selected={users.find(
                                (user) => user.id === values.user
                              )}
                              type="text"
                              onQueryChange={(search) => fechtUsers(search)}
                              options={users}
                              error={!!errors.user}
                              textError={errors.user}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </DialogCreate>
            );
          }}
        </Formik>

        <DialogCreate
          open={openUpImage}
          setOpen={handleOpenUpImage}
          tittle={t("share.views.up_image")}
          tittleButtonCreate={t("general.save")}
          tittleButtonCancel={t("general.cancel")}
          OnclickCreate={() => {}}
          OnclickCancel={handleOpenUpImage}
        >
          <form action="">
            <div className="mt-6 mx-2">
              <div className="space-y-12">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                  <div className="flex items-center justify-center h-full text-center">
                    <div>
                      <h2 className="text-base font-semibold leading-7 text-gray-900">
                        Imagen de Ingreso
                      </h2>
                    </div>
                  </div>
                  <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                    <div className="col-span-full">
                      <InputFile
                        name=""
                        id=""
                        label={t("general.image")}
                        spanButton={t("general.up_image")}
                        description={t("general.format_image")}
                        selectedImage={""}
                        onFileChange={() => {}}
                        onRemoveImage={() => {}}
                        tittleDelete={t("gym.views.delete_image")}
                      />
                      <p className="mt-2 text-xs text-center text-gray-500">
                        {t("access.views.image_access")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </DialogCreate>
      </div>
      {openFilter ? (
        <>
          <div className="w-full mx-auto mb-8">
            <div className="flex flex-1 self-stretch lg:gap-x-6 md:px-4 pb-2 border-b-2">
              <div className="fixed inset-1 h-min md:inset-x-4 top-16 lg:inset-x-64 lg:top-48 w-full lg:w-10/12 z-50 flex justify-center">
                <div
                  className="fixed inset-0 bg-black opacity-50"
                  onClick={handleOpenFilter}
                ></div>
                <div className="relative bg-white w-full max-w-md lg:max-w-3xl h-full lg:h-auto shadow-xl p-4 flex flex-col items-center">
                  <button
                    type="button"
                    className="absolute top-0 right-0 m-4 text-gray-400 hover:text-gray-500"
                    onClick={handleOpenFilter}
                  >
                    <span className="sr-only">Close</span>
                    &times;
                  </button>
                  <div className="flex flex-col lg:flex-row gap-4 justify-center items-center w-full">
                    <div className="col-span-full w-full lg:w-1/3">
                      <Select
                        name="selected_role"
                        span={t("general.mandatory")}
                        selected={
                          roleOptions
                            ? roleOptions.find(
                                (role) =>
                                  role.name == filterFormik.values.selected_role
                              )
                            : null
                        }
                        setSelected={(value) =>
                          filterFormik.setFieldValue(
                            "selected_role",
                            value.name
                          )
                        }
                        options={roleOptions}
                        tittle={t("user.views.role")}
                        error={!!filterFormik.errors.selected_role}
                        textError={filterFormik.errors.selected_role}
                      />
                    </div>
                    <div className="col-span-full w-full lg:w-1/3">
                      <DateRangePicker
                        id="date"
                        label={t("maintenance.views.inter_dates")}
                        value={{
                          startDate: filterFormik.values.date_init
                            ? DateTime.fromFormat(
                                filterFormik.values.date_init,
                                "yyyy-MM-dd"
                              ).toJSDate()
                            : new Date(),
                          endDate: filterFormik.values.date_end
                            ? DateTime.fromFormat(
                                filterFormik.values.date_end,
                                "yyyy-MM-dd"
                              ).toJSDate()
                            : new Date(),
                        }}
                        onDateChange={(startDate, endDate) => {
                          filterFormik.setFieldValue(
                            "date_init",
                            DateTime.fromJSDate(startDate).toFormat(
                              "yyyy-MM-dd"
                            )
                          );
                          filterFormik.setFieldValue(
                            "date_end",
                            DateTime.fromJSDate(endDate).toFormat("yyyy-MM-dd")
                          );
                        }}
                      />
                    </div>
                    <div className="col-span-full w-full lg:w-1/3">
                      <InputWithCornerHint
                        label={t("share.views.number_min_access")}
                        type="number"
                        value={filterFormik.values.number_access}
                        onChange={filterFormik.handleChange}
                        name="number_access"
                        placeholder={t("share.views.number_min_access")}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <></>
      )}
    </div>
  );
};
